import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { useController, useForm } from 'react-hook-form';

import type {
    Organization,
    Site,
    SiteAdaptiveSchema,
    SiteInsightsVisitorSegment,
} from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { createPalette } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    confirm,
    Description,
    Dialog,
    DialogActionButton,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    FieldError,
    FieldGroup,
    Fieldset,
    Form,
    FormWatcher,
    Heading,
    Label,
    LoadingIndicator,
    LoadingPanel,
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarItemList,
    SidebarLink,
    SidebarSection,
    SidebarSectionHeading,
    TextField,
    TextInput,
} from '@gitbook/spine-react';
import { deepEquals } from '@gitbook/util-immutable';
import { useDeferredFlush, useStateWithDeps } from '@gitbook/util-react-hooks';
import { createFastCacheKeyWithCollisionRisk } from '@gitbook/util-suspense-cache';

import { NavigationLink, useSetAnalyticsScreen } from '../../navigation';
import { SidebarActionsButton } from '../../Sidebars/SidebarActionsButton';
import { apiQuery } from '../../state';
import { APIErrorBoundary } from '../APIErrorBoundary';
import { JSONCodeEditor } from './JSONCodeEditor';
import type {
    SiteVisitorDetectedSegment,
    SiteVisitorSegment,
    UserDefinedVisitorSegmentData,
} from './types';
import { useUserDefinedVisitorSegments } from './useUserDefinedVisitorSegments';

export const DEFAULT_SEGMENT_TITLE = 'Untitled segment';

interface AdaptiveContentVisitorSegmentsDialogProps {
    organizationId: Organization['id'];
    siteId: Site['id'];
    selectedSegment?: SiteVisitorSegment;
    onConfirm: (selectedSegment: SiteVisitorSegment | null) => void;
}

/**
 * Dialog to browse, edit and delete test data of visitor segments stored locally (indexedDB).
 */
export function AdaptiveContentVisitorSegmentsDialog(
    props: AdaptiveContentVisitorSegmentsDialogProps
) {
    return (
        <Dialog size={{ width: 900, height: 600 }}>
            <React.Suspense fallback={<LoadingPanel size="large" />}>
                <APIErrorBoundary
                    fallback={{
                        404: (
                            <AdaptiveContentVisitorSegmentsDialogContent
                                {...props}
                                siteSchema={undefined}
                            />
                        ),
                    }}
                >
                    <AdaptiveContentVisitorSegmentsDialogWithSiteSchema {...props} />
                </APIErrorBoundary>
            </React.Suspense>
        </Dialog>
    );
}

function AdaptiveContentVisitorSegmentsDialogWithSiteSchema(
    props: AdaptiveContentVisitorSegmentsDialogProps
) {
    const { data: siteSchema } = useSuspenseQuery(
        apiQuery('getSiteAdaptiveSchema', {
            organizationId: props.organizationId,
            siteId: props.siteId,
        })
    );

    return <AdaptiveContentVisitorSegmentsDialogContent {...props} siteSchema={siteSchema} />;
}

function AdaptiveContentVisitorSegmentsDialogContent(
    props: AdaptiveContentVisitorSegmentsDialogProps & {
        siteSchema: SiteAdaptiveSchema | undefined;
    }
) {
    const { organizationId, siteId, siteSchema, selectedSegment, onConfirm } = props;

    const { addVisitorSegment, updateVisitorSegment } = useUserDefinedVisitorSegments();

    const [selectedVisitorSegment, setSelectedVisitorSegment] = useStateWithDeps<
        SiteVisitorSegment | undefined
    >(selectedSegment, [selectedSegment]);

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: siteId, organization: organizationId },
            {
                screen: 'adaptive_content_segment',
            }
        ),
        [siteId, organizationId]
    );

    const createNewUserDefinedSegment = async (
        newSegment: Pick<UserDefinedVisitorSegmentData, 'title' | 'claims'>
    ) => {
        const addedSegment = await addVisitorSegment({
            title: newSegment.title,
            claims: newSegment.claims,
        });
        analytics.logEvent({
            type: 'adaptive_content_add_segment',
        });
        setSelectedVisitorSegment({
            type: 'user-defined',
            ...addedSegment,
        });
    };

    const updateUserDefinedSegment = async (
        newSegmentValue: Omit<UserDefinedVisitorSegmentData, 'lastUpdatedAt'>
    ) => {
        const updatedSegment = await updateVisitorSegment(newSegmentValue);
        analytics.logEvent({
            type: 'adaptive_content_edit_segment',
        });
        setSelectedVisitorSegment({
            type: 'user-defined',
            ...updatedSegment,
        });
        return;
    };

    return (
        <div className="flex size-full">
            <Sidebar>
                <SidebarBody>
                    <SidebarSection>
                        <SidebarSectionHeading>Custom Segments</SidebarSectionHeading>
                        {selectedVisitorSegment ? (
                            <UserDefinedVisitorSegmentsSectionLinks
                                selectedVisitorSegment={selectedVisitorSegment}
                                setSelectedVisitorSegment={setSelectedVisitorSegment}
                            />
                        ) : null}
                    </SidebarSection>
                    <React.Suspense fallback={<SidebarSectionSkeleton />}>
                        <DetectedVisitorSegmentsSidebarSection
                            organizationId={organizationId}
                            siteId={siteId}
                            selectedVisitorSegment={selectedVisitorSegment}
                            setSelectedVisitorSegment={setSelectedVisitorSegment}
                            createNewVisitorSegment={createNewUserDefinedSegment}
                        />
                    </React.Suspense>
                </SidebarBody>
                <SidebarFooter>
                    <Button
                        className="self-start"
                        kind="secondary"
                        leadingIcon={Icon.Plus}
                        label="New segment"
                        onPress={async () =>
                            await createNewUserDefinedSegment({
                                title: DEFAULT_SEGMENT_TITLE,
                                claims: {},
                            })
                        }
                    />
                </SidebarFooter>
            </Sidebar>
            <div className="flex size-full flex-1 flex-col md:overflow-auto">
                <DialogBody className="size-full [&&]:pb-0">
                    {selectedVisitorSegment ? (
                        selectedVisitorSegment.type === 'user-defined' ? (
                            <EditUserDefinedVisitorSegmentDialogBody
                                siteId={siteId}
                                siteSchema={siteSchema}
                                segment={selectedVisitorSegment}
                                onSaveSegment={updateUserDefinedSegment}
                            />
                        ) : (
                            <DetectedVisitorSegmentDialogBody segment={selectedVisitorSegment} />
                        )
                    ) : (
                        <EmptyState>
                            <EmptyStateIcon>
                                <Icon.Alert />
                            </EmptyStateIcon>
                            <Heading>No segments</Heading>
                            <Description>No segments have been added yet.</Description>
                            <EmptyStateActions>
                                <Button
                                    kind="secondary"
                                    label="Add segment"
                                    onPress={async () =>
                                        await createNewUserDefinedSegment({
                                            title: DEFAULT_SEGMENT_TITLE,
                                            claims: {},
                                        })
                                    }
                                />
                            </EmptyStateActions>
                        </EmptyState>
                    )}
                </DialogBody>
                <DialogFooter className="border-t-0 md:[&&]mt-0 [&&]:mt-0">
                    {!siteSchema ? (
                        <Alert
                            kind="warning"
                            size="xsmall"
                            details={
                                <>
                                    <NavigationLink
                                        to={{
                                            screen: {
                                                type: 'site',
                                                organization: organizationId,
                                                site: siteId,
                                                tab: {
                                                    type: 'settings',
                                                    section: 'schema',
                                                },
                                            },
                                        }}
                                    >
                                        Add a schema
                                    </NavigationLink>{' '}
                                    to this site before you can setup adaptive content expressions.
                                </>
                            }
                        />
                    ) : null}
                    <DialogCloseButton />
                    <DialogActionButton
                        label="Confirm"
                        onPress={() => onConfirm(selectedVisitorSegment ?? null)}
                    />
                </DialogFooter>
            </div>
        </div>
    );
}

/**
 * Section of the dialog sidebar showing the list of user-defined segments.
 */
function UserDefinedVisitorSegmentsSectionLinks(props: {
    selectedVisitorSegment: SiteVisitorSegment;
    setSelectedVisitorSegment: React.Dispatch<React.SetStateAction<SiteVisitorSegment>>;
}) {
    const { selectedVisitorSegment, setSelectedVisitorSegment } = props;

    const { userDefinedSegments, addVisitorSegment, deleteVisitorSegment } =
        useUserDefinedVisitorSegments();

    const segments = userDefinedSegments || [];

    const createNewUserDefinedSegment = async (
        newSegment: Pick<UserDefinedVisitorSegmentData, 'title' | 'claims'>
    ) => {
        const addedSegment = await addVisitorSegment(newSegment);
        setSelectedVisitorSegment({
            type: 'user-defined',
            ...addedSegment,
        });
    };

    const deleteUserDefinedSegment = async (id: number) => {
        await deleteVisitorSegment(id);

        analytics.logEvent({
            type: 'adaptive_content_delete_segment',
        });
        const deletedSegmentIndex = segments.findIndex((segment) => segment.id === id);
        // Change the selected segment when the deleted one was selected
        if (selectedVisitorSegment.id === id) {
            const newSelectedSegment = segments.at(
                deletedSegmentIndex === 0 ? 1 : deletedSegmentIndex - 1
            );
            if (!newSelectedSegment) {
                return;
            }
            setSelectedVisitorSegment({
                type: 'user-defined',
                ...newSelectedSegment,
            });
        }
    };

    return (
        <React.Suspense fallback={<LoadingPanel size="small" />}>
            <SidebarItemList>
                {segments.map((segment) => (
                    <SidebarLink
                        key={segment.id}
                        label={segment.title}
                        isActive={segment.id === selectedVisitorSegment.id}
                        onClick={() =>
                            setSelectedVisitorSegment({ type: 'user-defined', ...segment })
                        }
                    >
                        <SidebarActionsButton
                            palette={() => {
                                return createPalette({
                                    groups: [
                                        {
                                            key: 'options',
                                            commands: [
                                                {
                                                    key: 'duplicate',
                                                    title: 'Duplicate',
                                                    icon: Icon.Duplicate,
                                                    trigger: async () => {
                                                        await createNewUserDefinedSegment({
                                                            title: `${segment.title} copy`,
                                                            claims: segment.claims,
                                                        });
                                                        analytics.logEvent({
                                                            type: 'adaptive_content_duplicate_segment',
                                                        });
                                                    },
                                                },
                                                {
                                                    key: 'delete',
                                                    title: 'Delete',
                                                    icon: Icon.Trashcan,
                                                    trigger: () => {
                                                        void confirm({
                                                            title: 'Delete visitor segment',
                                                            description: (
                                                                <>
                                                                    This custom visitor segment will
                                                                    be deleted.
                                                                </>
                                                            ),
                                                            kind: 'danger',
                                                            confirmLabel: 'Delete',

                                                            onConfirm: () =>
                                                                deleteUserDefinedSegment(
                                                                    segment.id
                                                                ),
                                                        });
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                });
                            }}
                        />
                    </SidebarLink>
                ))}
            </SidebarItemList>
        </React.Suspense>
    );
}

/**
 * Section of the dialog sidebar showing the list of auto-detected segments (Insights segments API).
 */
function DetectedVisitorSegmentsSidebarSection(props: {
    organizationId: Organization['id'];
    siteId: Site['id'];
    selectedVisitorSegment: SiteVisitorSegment | undefined;
    setSelectedVisitorSegment: React.Dispatch<React.SetStateAction<SiteVisitorSegment>>;
    createNewVisitorSegment: (
        newSegment: Pick<UserDefinedVisitorSegmentData, 'title' | 'claims'>
    ) => Promise<void>;
}) {
    const {
        organizationId,
        siteId,
        selectedVisitorSegment,
        setSelectedVisitorSegment,
        createNewVisitorSegment,
    } = props;

    const { data: listVisitorSegmentsResponse } = useSuspenseQuery(
        apiQuery('listSiteVisitorSegments', {
            organizationId,
            siteId,
        })
    );

    return listVisitorSegmentsResponse && listVisitorSegmentsResponse.items.length > 0 ? (
        <SidebarSection>
            <SidebarSectionHeading>Detected Segments</SidebarSectionHeading>
            {selectedVisitorSegment ? (
                <DetectedVisitorSegmentsSectionLinks
                    autoDetectedSegments={listVisitorSegmentsResponse.items}
                    selectedVisitorSegment={selectedVisitorSegment}
                    setSelectedVisitorSegment={setSelectedVisitorSegment}
                    createNewVisitorSegment={createNewVisitorSegment}
                />
            ) : null}
        </SidebarSection>
    ) : null;
}

function DetectedVisitorSegmentsSectionLinks(props: {
    autoDetectedSegments: SiteInsightsVisitorSegment[];
    selectedVisitorSegment: SiteVisitorSegment | undefined;
    setSelectedVisitorSegment: React.Dispatch<React.SetStateAction<SiteVisitorSegment>>;
    createNewVisitorSegment: (
        newSegment: Pick<UserDefinedVisitorSegmentData, 'title' | 'claims'>
    ) => Promise<void>;
}) {
    const {
        autoDetectedSegments,
        selectedVisitorSegment,
        setSelectedVisitorSegment,
        createNewVisitorSegment,
    } = props;

    const segments = autoDetectedSegments.map((segment) => ({
        type: 'detected',
        id: createFastCacheKeyWithCollisionRisk(segment.claims),
        ...segment,
    }));

    return (
        <React.Suspense fallback={<LoadingPanel size="small" />}>
            <SidebarItemList>
                {segments.map((segment: SiteVisitorDetectedSegment) => (
                    <SidebarLink
                        key={segment.id}
                        label={segment.title}
                        isActive={segment.id === selectedVisitorSegment?.id}
                        onClick={() => setSelectedVisitorSegment(segment)}
                    >
                        <SidebarActionsButton
                            palette={() => {
                                return createPalette({
                                    groups: [
                                        {
                                            key: 'options',
                                            commands: [
                                                {
                                                    key: 'edit',
                                                    title: 'Edit',
                                                    icon: Icon.Edit,
                                                    trigger: async () => {
                                                        await createNewVisitorSegment({
                                                            title: `${segment.title} copy`,
                                                            claims: segment.claims,
                                                        });
                                                        analytics.logEvent({
                                                            type: 'adaptive_content_duplicate_segment',
                                                        });
                                                    },
                                                },
                                            ],
                                        },
                                    ],
                                });
                            }}
                        />
                    </SidebarLink>
                ))}
            </SidebarItemList>
        </React.Suspense>
    );
}

function SidebarSectionSkeleton() {
    return (
        <div aria-busy className="flex h-36 flex-col items-center justify-items-center">
            <LoadingIndicator className="grow items-center" size="small" />
        </div>
    );
}

/**
 * Form shown in the body of the dialog that allows to edit a user-defined segment.
 */
function EditUserDefinedVisitorSegmentDialogBody(props: {
    siteId: Site['id'];
    siteSchema: SiteAdaptiveSchema | undefined;
    segment: Omit<UserDefinedVisitorSegmentData, 'lastUpdatedAt'>;
    onSaveSegment: (
        newUserDefinedSegment: Omit<UserDefinedVisitorSegmentData, 'lastUpdatedAt'>
    ) => Promise<void>;
}) {
    const { siteId, siteSchema, segment, onSaveSegment: onSave } = props;
    const debounce = useDeferredFlush<Promise<void>>(250);

    const form = useForm({
        mode: 'onChange',
        defaultValues: {
            title: segment.title,
            claims: JSON.stringify(segment.claims, null, 2),
        },
    });

    // Ensure form resets if the user switches between segments
    React.useEffect(() => {
        form.reset({
            title: segment.title,
            claims: JSON.stringify(segment.claims, null, 2),
        });
    }, [segment.id]);

    const { field: claimsDataField } = useController({
        name: 'claims',
        control: form.control,
        rules: {
            required: 'Required',
            minLength: 1,
            validate: {
                validJSON: (v) => {
                    try {
                        JSON.parse(v);
                        return true;
                    } catch (error) {
                        return `${error instanceof SyntaxError ? error.message : error}`;
                    }
                },
            },
        },
    });

    const saveNewSegmentValue = async (newSegmentValue: { title: string; claims: string }) => {
        const newClaims = JSON.parse(newSegmentValue.claims);
        if (segment.title === newSegmentValue.title && deepEquals(segment.claims, newClaims)) {
            return;
        }

        debounce(async () => {
            try {
                await onSave({
                    id: segment.id,
                    title: newSegmentValue.title,
                    claims: newClaims,
                });
            } catch (error) {
                if (error instanceof SyntaxError) {
                    form.setError('claims', {
                        type: 'validJSON',
                        message: error.message,
                    });
                }
            }
        });
    };

    return (
        <Form form={form}>
            <FormWatcher onChange={saveNewSegmentValue} />
            <Fieldset className="h-full">
                <FieldGroup className="flex-none">
                    <TextField name="title" minLength={1} maxLength={125} required>
                        <Label>Segment name</Label>
                        <TextInput autoFocus />
                        <FieldError />
                    </TextField>
                </FieldGroup>
                <FieldGroup>
                    <Label>Segment JSON</Label>
                    <JSONCodeEditor
                        value={claimsDataField.value}
                        onChange={claimsDataField.onChange}
                        validationSettings={
                            siteSchema
                                ? {
                                      level: 'warning',
                                      jsonSchema: {
                                          uri: `in-memory://${siteId}-adaptive-schema`,
                                          fileMatch: ['*'],
                                          schema: siteSchema.jsonSchema,
                                      },
                                  }
                                : undefined
                        }
                    />
                </FieldGroup>
            </Fieldset>
        </Form>
    );
}

/**
 * Section shown in the body of the dialog that displays an auto-detected segment.
 */
function DetectedVisitorSegmentDialogBody(props: { segment: SiteVisitorDetectedSegment }) {
    const { segment } = props;

    return (
        <Fieldset className="h-full">
            <FieldGroup className="flex-none">
                <Label>Test data name</Label>
                <TextInput readOnly value={segment.title} />
            </FieldGroup>
            <FieldGroup className="grow overflow-auto">
                <Label>Test data JSON</Label>
                <div className="max-h-none grow overflow-y-auto rounded border bg-secondary">
                    <React.Suspense fallback={<LoadingPanel size="small" />}>
                        <JSONCodeEditor value={JSON.stringify(segment.claims, null, 2)} readOnly />
                    </React.Suspense>
                </div>
            </FieldGroup>
        </Fieldset>
    );
}
